import React, { useEffect, useReducer, useState } from 'react';
import {
  Button,
  FormGroup,
  InputGroup,
  InputGroupText,
  Label,
  Tooltip,
} from 'reactstrap';
import { Communicator } from '../tools/Communication';
import { ContainerType } from './ContainerComponents/ContainerPage';
import { ResponsiveTable } from '../components/Layout';
import { FaDownload, FaSearch } from 'react-icons/fa';
import { expandNamesByLanguage, UserLanguage } from '@/utils/translation';
import { ContentSection } from '../components/Layout/ContentSection';
import { MultiSelect } from '@mantine/core';
import Fuse from 'fuse.js';
import CustomPagination from '../components/CustomPagination';
import ContainerTypeDisplay from '../components/ContainerType/ContainerTypeDisplay';
import i18n from '@/utils/i18n';
import { ContainerTypeLabelGenerationModal } from './ContainerTypes/ContainerTypeLabelGenerationModal';

const PAGE_SIZE = 10;

const columns = [
  { id: 1, value: 'image', label: 'Image' },
  { id: 2, value: 'name', label: 'Name' },
  { id: 3, value: 'countries', label: 'Countries' },
  { id: 4, value: 'class', label: 'Class' },
  // { id: 5, value: 'group', label: 'Group' },
  { id: 6, value: 'sizeHelper', label: 'Size Helper' },
  // { id: 7, value: 'color', label: 'Container Box Color Hex Code' },
  { id: 8, value: 'packingUnit', label: 'Packing Unit' },
  { id: 9, value: 'sku', label: 'SKU' },
  // { id: 10, value: 'branding', label: 'Branding' },
  // { id: 11, value: 'legalOwner', label: 'Legal Owner' },
  { id: 12, value: 'packingInstructions', label: 'Packing Instructions' },
  // { id: 13, value: 'weight', label: 'Weight' },
  // { id: 14, value: 'archived', label: 'Archived' },
  { id: 15, value: 'label', label: 'Label Sticker' },
];

function ContainerTypesView() {
  const [allContainerTypes, setAllContainerTypes] = useState<ContainerType[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [language] = useState(UserLanguage.En);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived] = useState(false);
  const [containerClasses, setContainerClasses] = useState<
    { value: string; label: string }[]
  >([]);
  const [classFilter, setClassFilter] = useState<string[]>(['EventPortfolio']);
  const [containerTypeForLabelModal, setContainerTypeForLabelModal] =
    useState<ContainerType | null>(null);

  const filteredContainerTypes = React.useMemo(() => {
    let filtered = allContainerTypes;

    filtered = filtered.filter((f) => f.archived === showArchived);

    if (classFilter.length > 0) {
      filtered = filtered.filter((ct) => classFilter.includes(ct.class));
    }

    if (searchTerm.length >= 3) {
      const fuse = new Fuse<ContainerType>(allContainerTypes, {
        keys: ['id', 'name', 'sku', 'branding', 'legalOwner'],
        includeScore: true,
        threshold: 0.3,
      });

      const results = fuse.search(searchTerm);
      filtered = results.map((result) => result.item);
    }

    if (!Number.isNaN(parseInt(searchTerm))) {
      const id = parseInt(searchTerm);
      filtered = filtered.filter((ct) => ct.id === id);
    }

    return filtered;
  }, [allContainerTypes, searchTerm, showArchived, classFilter]);

  const paginatedContainerTypes = React.useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    setPageCount(Math.ceil(filteredContainerTypes.length / PAGE_SIZE));
    return filteredContainerTypes.slice(startIndex, endIndex);
  }, [filteredContainerTypes, currentPage]);

  const columnFunctions: Record<string, any> = React.useMemo(
    () => ({
      image: (containerType: ContainerType) => (
        <ContainerTypeDisplay
          containerType={containerType}
          showName={false}
          showContainerId={false}
        />
      ),
      name: (containerType: ContainerType) => (
        <span title={containerType.namesByLanguage[language]}>
          {containerType.namesByLanguage[language]}
        </span>
      ),
      countries: (containerType: ContainerType) => (
        <span title={containerType.countryCodes?.join(', ') || ''}>
          {containerType.countryCodes
            ?.map((c) => c.replace(/[\{\}]/g, ''))
            .join(', ') || ''}
        </span>
      ),
      class: (containerType: ContainerType) => (
        <span title={containerType.class}>{containerType.class}</span>
      ),
      sizeHelper: (containerType: ContainerType) => (
        <span title={containerType.sizeHelper}>{containerType.sizeHelper}</span>
      ),
      color: (containerType: ContainerType) => (
        <div className="d-flex align-items-center">
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: `#${containerType.hexCode}`,
              marginRight: '8px',
              border: '1px solid #ccc',
            }}
          />
          <span title={containerType.hexCode || ''}>
            {containerType.hexCode || ''}
          </span>
        </div>
      ),
      packingUnit: (containerType: ContainerType) => (
        <span
          className="numbers-formatted"
          title={containerType.packingUnit.toString()}
        >
          {containerType.packingUnit}
        </span>
      ),
      sku: (containerType: ContainerType) => (
        <span title={containerType.sku}>{containerType.sku}</span>
      ),
      branding: (containerType: ContainerType) => (
        <span title={containerType.branding}>{containerType.branding}</span>
      ),
      legalOwner: (containerType: ContainerType) => (
        <span title={containerType.legalOwner}>{containerType.legalOwner}</span>
      ),
      packingInstructions: (containerType: ContainerType) => (
        <span title={containerType.packingInstructions}>
          {containerType.packingInstructions}
        </span>
      ),
      weight: (containerType: ContainerType) => (
        <span
          className="numbers-formatted"
          title={containerType.weight.toString()}
        >
          {containerType.weight}
        </span>
      ),
      archived: (containerType: ContainerType) => (
        <span>{containerType.archived ? 'Yes' : 'No'}</span>
      ),
      label: (containerType: ContainerType) => {
        return (
          <DownloadLabelButton
            containerType={containerType}
            showModal={setContainerTypeForLabelModal}
          />
        );
      },
    }),
    [language],
  );

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const promises = [
      Communicator.Get('/Container/GetAllContainerCategories', {})
        .then((response) => response.json())
        .then((data: { containerTypes: ContainerType[] }) => {
          if (data.containerTypes) {
            setAllContainerTypes(
              data.containerTypes
                .map((ct) => expandNamesByLanguage(ct) as ContainerType)
                .sort((a, b) => a.id - b.id),
            );
          }
        }),
      Communicator.Get('/Container/GetAllContainerClasses', {})
        .then((response) => response.json())
        .then((containerClasses: { id: string; name: string }[]) =>
          setContainerClasses(
            containerClasses.map((cc) => ({ value: cc.name, label: cc.name })),
          ),
        ),
    ];
    await Promise.all(promises);
    setIsLoading(false);
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <ContentSection
      title={i18n.t('ContainerTypes:containerTypes')}
      collapsable={false}
    >
      <FormGroup>
        <Label htmlFor="search">Search Container Types</Label>
        <InputGroup>
          <InputGroupText>
            <FaSearch />
          </InputGroupText>
          <input
            id="search"
            className="form-control"
            type="text"
            placeholder="Search by ID, name, SKU, branding, or legal owner..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </InputGroup>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="classFilter">Filter by Class</Label>
        <MultiSelect
          id="classFilter"
          data={containerClasses}
          searchable
          value={classFilter}
          onChange={(value) => {
            setClassFilter(value);
            setCurrentPage(1);
          }}
          placeholder="Select classes to filter..."
        />
      </FormGroup>

      <ResponsiveTable>
        <thead>
          <tr>
            <th>Id</th>
            {columns.map((column) => (
              <th key={column.id}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedContainerTypes.map((containerType) => {
            return (
              <tr key={containerType.id}>
                <td className="numbers-formatted">{containerType.id}</td>
                {columns.map((column) => {
                  const columnFunction = columnFunctions[column.value];
                  return (
                    <td key={column.id}>{columnFunction(containerType)}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </ResponsiveTable>

      <CustomPagination
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      {containerTypeForLabelModal && (
        <ContainerTypeLabelGenerationModal
          containerType={containerTypeForLabelModal}
          onClose={() => setContainerTypeForLabelModal(null)}
        />
      )}
    </ContentSection>
  );
}

function DownloadLabelButton({
  containerType,
  showModal,
}: {
  containerType: ContainerType;
  showModal: (containerType: ContainerType) => void;
}) {
  const [tooltipVisible, toogleTooltip] = useReducer((p) => !p, false);

  const disabled = containerType.class !== 'EventPortfolio';

  return (
    <>
      <Button
        id="tooltip-download-label"
        color="secondary"
        size="sm"
        onClick={() => {
          if (disabled) {
            return;
          }
          showModal(containerType);
        }}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <FaDownload /> {i18n.t('Common:download')}
      </Button>
      <Tooltip
        target="tooltip-download-label"
        isOpen={tooltipVisible}
        toggle={toogleTooltip}
        delay={disabled ? 50 : 500}
      >
        {disabled
          ? 'Only for Event Portfolio containers'
          : 'Download label for print'}
      </Tooltip>
    </>
  );
}

export default ContainerTypesView;
